/** Canal `generex-auth` entre les onglets du navigateur (docs/17 §4.5, §4.8) : un navigateur porte une seule session,
 *  tous ses onglets suivent la dernière connexion. Chaque onglet y diffuse `{kind, scope_ref}` à chaque connexion,
 *  déconnexion, changement d'utilisateur, verrouillage et déverrouillage, et l'heure de sa dernière activité.
 *
 *  Réaction d'un onglet (`reactionTo`), selon le compte pour lequel son application est montée :
 *  - même compte : un déverrouillage ou une reconnexion retire sa surcouche et relance ses files ; un verrouillage
 *    pose la sienne ; une déconnexion annoncée (`logout-pending`) lui fait envoyer ce qui attend, puis la
 *    déconnexion efface les brouillons de la personne et recharge l'onglet ; un changement d'utilisateur le fait
 *    ranger ses saisies et se recharger ;
 *  - autre compte, ou aucun : une connexion lui fait ranger ses saisies sous sa propre portée, arrêter ses files et se
 *    recharger, pour remonter sur la session présente.
 *  Navigateur sans `BroadcastChannel` : l'en-tête `X-Generex-Compte` suffit (la garde répond 409). */

export const AUTH_CHANNEL = "generex-auth";

export type AuthMessage =
  | { kind: "login" | "logout" | "switch" | "unlock" | "lock"; scope_ref: string | null }
  | { kind: "activity"; scope_ref: string | null; at: number }
  | { kind: "logout-pending"; scope_ref: string; id: string }
  | { kind: "flush-start"; id: string; tab: string }
  | { kind: "flush-done"; id: string; tab: string; unsaved: string[] };

export type Reaction = "ignore" | "activity" | "resume" | "lock" | "flush" | "erase" | "reload";

/** Ce que fait un onglet d'un message, selon le compte de son application montée (`mounted`, null : aucun) et ce
 *  qu'il affiche (`app` : l'application ; `screen` : écran de connexion, proposition de bascule, notice). */
export function reactionTo(message: AuthMessage, mounted: string | null, shows: "app" | "screen"): Reaction {
  const mine = mounted !== null && "scope_ref" in message && message.scope_ref === mounted;
  switch (message.kind) {
    case "activity":
      return mine ? "activity" : "ignore";
    case "login":
    case "unlock":
      if (mine) return "resume";
      // Autre compte, ou application montée sans compte (mode hérité, repli), ou écran sans session : la session de
      // ce navigateur a changé, l'onglet remonte sur elle.
      return mounted !== null || shows === "app" || message.kind === "login" ? "reload" : "ignore";
    case "lock":
      return mine ? "lock" : "ignore";
    case "logout-pending":
      return mine ? "flush" : "ignore";
    case "logout":
      return mine ? "erase" : "ignore";
    case "switch":
      return mine ? "reload" : "ignore";
    default:
      return "ignore";
  }
}

/** Canal minimal (celui du navigateur, ou un double pour les essais). */
export interface ChannelLike {
  postMessage(message: unknown): void;
  addEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  removeEventListener(type: "message", listener: (event: MessageEvent) => void): void;
  close(): void;
}

function browserChannel(): ChannelLike | null {
  try {
    return typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(AUTH_CHANNEL);
  } catch {
    return null;
  }
}

/** Identifiant de cet onglet (réponses à une déconnexion annoncée). */
export const TAB_ID = Math.random().toString(36).slice(2, 10);

const isMessage = (value: unknown): value is AuthMessage =>
  Boolean(value && typeof value === "object" && typeof (value as { kind?: unknown }).kind === "string");

/** Canal `generex-auth` de ce document. Sans `BroadcastChannel`, il ne fait rien. */
export class AuthChannel {
  private channel: ChannelLike | null;
  private listeners = new Set<(message: AuthMessage) => void>();
  private readonly onMessage = (event: MessageEvent) => {
    if (!isMessage(event.data)) return;
    for (const listener of this.listeners) listener(event.data);
  };

  constructor(factory: () => ChannelLike | null = browserChannel) {
    this.channel = factory();
    this.channel?.addEventListener("message", this.onMessage);
  }

  get available(): boolean {
    return this.channel !== null;
  }

  post(message: AuthMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch {
      /* canal fermé : l'en-tête du compte reste le filet */
    }
  }

  listen(listener: (message: AuthMessage) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close(): void {
    this.channel?.removeEventListener("message", this.onMessage);
    this.channel?.close();
    this.channel = null;
    this.listeners.clear();
  }
}

/** Déconnexion annoncée : attend que les autres onglets du même compte aient envoyé ce qui attend (réponses
 *  `flush-start`, puis `flush-done`), au plus `timeoutMs` ; rend les saisies qu'ils n'ont pas pu enregistrer. Un onglet
 *  qui ne répond pas dans les `startMs` premières millisecondes n'est pas attendu. */
export function collectOtherTabs(
  channel: AuthChannel,
  scope: string,
  { timeoutMs = 4000, startMs = 250, id = Math.random().toString(36).slice(2) }: { timeoutMs?: number; startMs?: number; id?: string } = {},
): Promise<string[]> {
  return new Promise((resolve) => {
    const started = new Set<string>();
    const done = new Set<string>();
    const unsaved: string[] = [];
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      stop();
      clearTimeout(deadline);
      clearTimeout(firstWait);
      resolve(unsaved);
    };
    const check = () => {
      if ([...started].every((tab) => done.has(tab))) finish();
    };
    let startedWindowOver = false;
    const stop = channel.listen((message) => {
      if ((message.kind !== "flush-start" && message.kind !== "flush-done") || message.id !== id) return;
      if (message.kind === "flush-start") started.add(message.tab);
      else {
        done.add(message.tab);
        unsaved.push(...message.unsaved);
        if (startedWindowOver) check();
      }
    });
    const firstWait = setTimeout(() => {
      startedWindowOver = true;
      check();
    }, startMs);
    const deadline = setTimeout(finish, timeoutMs);
    channel.post({ kind: "logout-pending", scope_ref: scope, id });
    if (!channel.available) finish();
  });
}
